<i18n lang="yaml" locale="en">
  fullscreen: Fullscreen
  start/stop: Start/Stop
  ignore: Ignore
  continue: Continue
  startover: Startover
  add preview long: Add a thumbnail by dropping it here.
  add preview short: Add as thumbnail.
  thumbnail: Thumbnail
  mode: Mode
  fill box: Fill box
  mute: Mute
  autoplay: Autoplay
  video settings: Video settings
  caption: Caption
  click behaviour: Click behaviour
  name: Name
</i18n>

<i18n lang="yaml" locale="de">
  fullscreen: Vollbild
  start/stop: Start/Stop
  ignore: Ignorieren
  continue: Fortsetzen
  startover: Von Anfang
  add preview long: Fügen Sie ein Vorschaubild hinzu, indem Sie es hier ablegen.
  add preview short: Als Vorschaubild hinzufügen.
  thumbnail: Vorschaubild
  mode: Modus
  fill box: Box ausfüllen
  mute: Stummschalten
  autoplay: Autoplay
  video settings: Video-Einstellungen
  caption: Beschriftung
  click behaviour: Klickverhalten
  name: Name
</i18n>

<template>
  <div
    @click.capture="handleFullscreen"
    class="video"
    :class="{
      'asset-browser-item': !isInProject && !isInPropEditor,
      'fill-box': this.fillBox}"
  >
    <video
      :key="thumbnail"
      class="content"
      ref="player"
      :autoplay="false"
      :controls="!editMode && (clickBehaviour === 'control' || isFullscreen)"
      :loop="true"
      :muted="muted"
      :src="src"
      @play="onPlay"
      @pause="onPause"
      @volumechange="onVolumeChange"
      controlsList="nodownload nofullscreen"
      :poster="thumbnail"
    />
    <div
      v-if="isFullscreen && caption"
      class="caption"
    >
      {{ caption }}
    </div>
    <div
      v-if="!isInProject || isInPropEditor"
      class="name"
    >
      {{ name }}
    </div>
    <keep-alive>
      <FileEditor
        v-if="$editMode && !isInPropEditor"
        ref="editor"
      />
    </keep-alive>
  </div>
</template>

<script>
import FileEditor from '@/components/editor/FileEditor'
import EventBus from '@/event-bus/eventBus'
import BaseModule from '@/components/modules/BaseModule'
import translatable from '@/store/mixins/translatable'

export default {
  // eslint-disable-next-line vue/name-property-casing
  name: 'video',
  extends: BaseModule,
  customStore: translatable,
  components: {
    FileEditor
  },
  inject: { $grid: { from: '$grid', default: undefined } },
  data () {
    return {
      playing: false,
      userMuted: undefined
    }
  },
  props: {
    isInPropEditor: {
      type: Boolean,
      default: false
    },
    isFullscreen: {
      type: Boolean,
      default: false
    },
    src: {
      type: String,
      required: true
    },
    ratio: {
      type: Number,
      default: undefined,
      min: 0
    },
    name: {
      type: String,
      default: '',
      input: 'text',
      label: 'name',
      condition: (values, path) => path.startsWith('/assets/')
    },
    // eslint-disable-next-line
    _headlineClickBehaviour: {
      input: 'heading',
      label: 'click behaviour',
      condition: (values, path) => !path.startsWith('/assets/')
    },
    clickBehaviour: {
      type: String,
      default: 'fullscreen',
      validator (value) {
        return ['fullscreen', 'control', 'none'].includes(value)
      },
      input: 'radio',
      choices: {
        fullscreen: 'fullscreen',
        control: 'start/stop',
        none: 'ignore'
      },
      condition: (values, path) => !path.startsWith('/assets/')
    },
    // eslint-disable-next-line
    _headlineCaption: {
      input: 'heading',
      label: 'caption',
      condition: (values, path) => !path.startsWith('/assets/')
    },
    caption: {
      type: String,
      default: '',
      input: 'text',
      condition: (values, path) => !path.startsWith('/assets/')
    },
    // eslint-disable-next-line
    _headlineVideoSettings: {
      input: 'heading',
      label: 'video settings',
      condition: (values, path) => !path.startsWith('/assets/')
    },
    autoplay: {
      label: 'autoplay',
      input: 'checkbox',
      type: Boolean,
      default: false,
      condition: (values, path) => !path.startsWith('/assets/')
    },
    muted: {
      label: 'mute',
      input: 'checkbox',
      type: Boolean,
      default: false,
      condition: (values, path) => !path.startsWith('/assets/')
    },
    fillBox: {
      label: 'fill box',
      input: 'checkbox',
      type: Boolean,
      default: false,
      condition: (values, path) => !path.startsWith('/assets/')
    },
    // eslint-disable-next-line
    _headlineMode: {
      input: 'heading',
      label: 'mode',
      condition: (values, path) => !path.startsWith('/assets/')
    },
    mode: {
      input: 'radio',
      choices: {
        continue: 'continue',
        startover: 'startover'
      },
      type: String,
      default: 'continue',
      validator (value) {
        return ['continue', 'startover'].includes(value)
      },
      condition: (values, path) => !path.startsWith('/assets/')
    },
    // eslint-disable-next-line
    _headlineThumbnail: {
      input: 'heading',
      label: 'thumbnail',
      condition: (values, path) => !path.startsWith('/assets/')
    },
    thumbnail: {
      input: 'drop',
      type: String,
      contentType: 'image',
      placeholder: 'add preview long',
      tooltip: 'add preview short',
      default: '',
      condition: (values, path) => !path.startsWith('/assets/')
    }
  },
  computed: {
    editMode () {
      return this.$editMode
    },
    isInProject () {
      return !this.$store.getters.path.startsWith('/assets/')
    },
    gridActive () {
      return this.$grid && this.$grid.$parent && this.$grid.$parent.isActive
    },
    isMuted () {
      return this.userMuted !== undefined ? this.userMuted : this.muted
    }
  },
  methods: {
    handleFullscreen () {
      if (this.isInProject && !this.$editMode && !this.isFullscreen && this.clickBehaviour === 'fullscreen') {
        this.$router.push({ path: this.$store.getters.path, query: { fullscreen: true } })
      }
    },
    onPlay () {
      this.playing = true
      this.emitGlobalPlay()
    },
    onPause () {
      this.emitGlobalStop()
      this.playing = false
    },
    onVolumeChange (event) {
      this.userMuted = event.srcElement.muted
      if (this.playing) {
        this.emitGlobalPlay()
      }
    },
    onGlobalPlay (event) {
      if (!event.muted && event.path !== this.$store.getters.path && this.playing && !this.isMuted) {
        this.pause()
      } else if (event.path === this.$store.getters.path && !this.playing) {
        this.$refs.player.play()
      }
    },
    emitGlobalPlay () {
      EventBus.$emit('media-is-playing', {
        path: this.$store.getters.path,
        muted: this.isMuted
      })
    },
    emitGlobalStop () {
      EventBus.$emit('media-is-stopped', {
        path: this.$store.getters.path
      })
    },
    play () {
      // don't play in editMode
      // don't play with autoplay disabled
      if (this.autoplay && this.gridActive && !this.$editMode) {
        if (!this.playing) {
          if (this.mode === 'startover') {
            this.$refs.player.currentTime = 0
          }
          EventBus.$emit('media-autoplay-request', {
            path: this.$store.getters.path,
            muted: this.isMuted
          })
        }
      }
    },
    pause () {
      if (this.playing) {
        this.emitGlobalStop()
        this.$refs.player.pause()
      }
    },
    getSize () {
      return new Promise((resolve, reject) => {
        this.$refs.player.addEventListener('loadeddata', () => {
          resolve({
            width: this.$refs.player.videoWidth,
            height: this.$refs.player.videoHeight
          })
        }, { once: true })
      })
    }
  },
  mounted () {
    // register playing event listener
    EventBus.$on('media-is-playing', this.onGlobalPlay)
    this.play()
  },
  destroyed () {
    EventBus.$off('media-is-playing', this.onGlobalPlay)
  },
  activated () {
    this.play()
  },
  deactivated () {
    this.pause()
  },
  watch: {
    '$editMode' (editMode) {
      if (editMode) {
        this.pause()
      } else {
        this.play()
      }
    },
    gridActive (gridActive) {
      if (gridActive) {
        this.play()
      } else {
        this.pause()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.video {
  position: relative;
  overflow: hidden;

  .content {
    max-height: 100%;
    max-width: 100%;
  }

  &.fill-box > .content {
    object-fit: cover;
    max-height: none;
    max-width: none;
    width: 100% !important;
    height: 100% !important;
  }
}

.video.fullscreen {
  display: flex;
  justify-content: center;

  .content {
    width: 100%;
    height: 100%;
  }
}
</style>
